import React, { useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import SEOHead from '../components/common/SEOHead';
import { usePageSeo } from '../hooks/useSeoConfig';
import api from '../services/api';

export default function Contact() {
    const { language } = useLanguage();
    const { user } = useAuth();
    const seo = usePageSeo('page_contact');

    const [formData, setFormData] = useState({
        userName: user ? `${user.firstName || ''} ${user.lastName || ''}`.trim() : '',
        userEmail: user?.email || '',
        subject: '',
        message: '',
    });
    const [sending, setSending] = useState(false);
    const [success, setSuccess] = useState(false);
    const [error, setError] = useState('');

    const handleChange = (e) => {
        setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setSending(true);

        try {
            await api.post('/contact.php', formData);
            setSuccess(true);
            setFormData(prev => ({ ...prev, subject: '', message: '' }));
        } catch (err) {
            setError(language === 'fr'
                ? 'Erreur lors de l\'envoi. Veuillez réessayer.'
                : 'Failed to send. Please try again.');
        } finally {
            setSending(false);
        }
    };

    return (
        <>
            <SEOHead
                title={language === 'fr' ? (seo.title_fr || 'Contactez-nous') : (seo.title_en || 'Contact Us')}
                description={language === 'fr' ? seo.desc_fr : seo.desc_en}
                keywords={language === 'fr' ? seo.keywords_fr : seo.keywords_en}
                canonical="/contact"
            />
            <div className="max-w-2xl mx-auto">
                {/* Header */}
                <div className="text-center mb-10">
                    <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 dark:bg-blue-500/10 rounded-2xl mb-4">
                        <svg className="w-8 h-8 text-blue-600 dark:text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                        </svg>
                    </div>
                    <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
                        {language === 'fr' ? 'Nous contacter' : 'Contact us'}
                    </h1>
                    <p className="text-gray-600 dark:text-gray-400">
                        {language === 'fr'
                            ? 'Une question ? Un problème ? Envoyez-nous un message.'
                            : 'A question? A problem? Send us a message.'}
                    </p>
                </div>

                {/* Success message */}
                {success && (
                    <div className="mb-6 p-5 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-2xl text-center">
                        <svg className="w-10 h-10 text-green-500 mx-auto mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <p className="text-green-700 dark:text-green-400 font-medium">
                            {language === 'fr' ? 'Message envoyé avec succès !' : 'Message sent successfully!'}
                        </p>
                        <p className="text-green-600 dark:text-green-500 text-sm mt-1">
                            {language === 'fr'
                                ? 'Nous vous répondrons dans les plus brefs délais.'
                                : 'We will respond as soon as possible.'}
                        </p>
                        <button
                            onClick={() => setSuccess(false)}
                            className="mt-3 px-4 py-2 text-sm text-green-700 dark:text-green-400 hover:bg-green-100 dark:hover:bg-green-900/30 rounded-lg transition"
                        >
                            {language === 'fr' ? 'Envoyer un autre message' : 'Send another message'}
                        </button>
                    </div>
                )}

                {/* Form */}
                {!success && (
                    <div className="bg-white dark:bg-white/[0.03] rounded-2xl shadow-lg dark:shadow-none border border-gray-200 dark:border-gray-800 p-6 md:p-8">
                        {error && (
                            <div className="mb-5 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                                <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
                            </div>
                        )}

                        <form onSubmit={handleSubmit} className="space-y-5">
                            {/* Name */}
                            <div>
                                <label htmlFor="userName" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                                    {language === 'fr' ? 'Nom' : 'Name'}
                                </label>
                                <input
                                    id="userName"
                                    name="userName"
                                    type="text"
                                    value={formData.userName}
                                    onChange={handleChange}
                                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-700 rounded-xl bg-white dark:bg-white/[0.03] text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                                    placeholder={language === 'fr' ? 'Votre nom' : 'Your name'}
                                />
                            </div>

                            {/* Email */}
                            <div>
                                <label htmlFor="userEmail" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                                    {language === 'fr' ? 'Courriel' : 'Email'} *
                                </label>
                                <input
                                    id="userEmail"
                                    name="userEmail"
                                    type="email"
                                    value={formData.userEmail}
                                    onChange={handleChange}
                                    required
                                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-700 rounded-xl bg-white dark:bg-white/[0.03] text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                                    placeholder={language === 'fr' ? 'vous@exemple.com' : 'you@example.com'}
                                />
                            </div>

                            {/* Subject */}
                            <div>
                                <label htmlFor="subject" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                                    {language === 'fr' ? 'Sujet' : 'Subject'} *
                                </label>
                                <input
                                    id="subject"
                                    name="subject"
                                    type="text"
                                    value={formData.subject}
                                    onChange={handleChange}
                                    required
                                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-700 rounded-xl bg-white dark:bg-white/[0.03] text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                                    placeholder={language === 'fr' ? 'En quoi pouvons-nous vous aider ?' : 'How can we help you?'}
                                />
                            </div>

                            {/* Message */}
                            <div>
                                <label htmlFor="message" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                                    {language === 'fr' ? 'Message' : 'Message'} *
                                </label>
                                <textarea
                                    id="message"
                                    name="message"
                                    value={formData.message}
                                    onChange={handleChange}
                                    required
                                    rows={6}
                                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-700 rounded-xl bg-white dark:bg-white/[0.03] text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition resize-none"
                                    placeholder={language === 'fr' ? 'Décrivez votre question ou problème...' : 'Describe your question or issue...'}
                                />
                            </div>

                            {/* Submit */}
                            <button
                                type="submit"
                                disabled={sending}
                                className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-6 rounded-xl transition duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {sending
                                    ? (language === 'fr' ? 'Envoi en cours...' : 'Sending...')
                                    : (language === 'fr' ? 'Envoyer le message' : 'Send message')}
                            </button>
                        </form>
                    </div>
                )}
            </div>
        </>
    );
}
